import { normalizeLocale, type SupportedLocale } from '@/i18n/config';

export type WorkspaceFilesCopy = {
  pathAriaLabel: string;
  personalFiles: string;
  /** Localized "{name}'s home" template; `{name}` is the viewer's first name. */
  selectFolder: string;
  personalFilesUnavailable: string;
  couldNotLoadFile: string;
  permissionDenied: string;
  generationFailed: string;
  generationInProgress: string;
  pathNotFound: string;
  openParentFolder: string;
  openPersonalFiles: string;
  tryAgain: string;
  addToFavorites: string;
  removeFromFavorites: string;
  addFavoriteError: string;
  removeFavoriteError: string;
  workspace: string;
  avatars: string;
  chats: string;
  documents: string;
  downloads: string;
  pictures: string;
  media: string;
  mediaEmpty: string;
  mediaFilterAll: string;
  mediaKindImage: string;
  mediaKindVideo: string;
  mediaKindAudio: string;
  mediaKindThreeD: string;
  mediaBackToGrid: string;
  shared: string;
  sharedWithMe: string;
  projects: string;
  products: string;
  brands: string;
  trash: string;
  restore: string;
  restoreError: string;
  users: string;
  fileLocationsAriaLabel: string;
  searchFiles: string;
  searchFilesPlaceholder: string;
  clearFileSearch: string;
  upload: string;
  uploadFiles: string;
  uploadFolder: string;
  invalidUploadFolder: string;
  newFolder: string;
  folderName: string;
  folderNamePlaceholder: string;
  createFolder: string;
  renameFolder: string;
  cancel: string;
  folderAlreadyExists: string;
  invalidFolderName: string;
  createFolderError: string;
  renameFolderError: string;
  folderEmpty: string;
  noFilesMatchFilter: string;
  item: string;
  items: string;
};

type WorkspaceFilesTableCopy = {
  name: string;
  source: string;
  created: string;
  modified: string;
  kind: string;
  size: string;
  directory: string;
  uploads: string;
};

const COPY_BY_LOCALE: Record<SupportedLocale, WorkspaceFilesCopy> = {
  en: {
    pathAriaLabel: 'Workspace path',
    personalFiles: 'My Drive',
    selectFolder: 'Select folder',
    personalFilesUnavailable: 'Could not open My Drive.',
    couldNotLoadFile: 'Could not load workspace file',
    permissionDenied: 'Permission denied',
    generationFailed: 'Generation failed',
    generationInProgress: 'Generation in progress',
    pathNotFound: 'Workspace path not found',
    openParentFolder: 'Open parent folder',
    openPersonalFiles: 'Open My Drive',
    tryAgain: 'Try again',
    addToFavorites: 'Add to Starred',
    removeFromFavorites: 'Remove from Starred',
    addFavoriteError: 'Could not add to Starred.',
    removeFavoriteError: 'Could not remove from Starred.',
    workspace: 'Workspace',
    avatars: 'Avatars',
    chats: 'Chats',
    documents: 'Documents',
    downloads: 'Downloads',
    pictures: 'Pictures',
    media: 'Media library',
    mediaEmpty: 'No media files in My Drive yet.',
    mediaFilterAll: 'All',
    mediaKindImage: 'Image',
    mediaKindVideo: 'Video',
    mediaKindAudio: 'Audio',
    mediaKindThreeD: '3D',
    mediaBackToGrid: 'Back to grid',
    shared: 'Shared',
    sharedWithMe: 'Shared with me',
    projects: 'Projects',
    products: 'Products',
    brands: 'Brands',
    trash: 'Trash',
    restore: 'Put Back',
    restoreError: 'Could not restore this item.',
    users: 'Users',
    fileLocationsAriaLabel: 'File locations',
    searchFiles: 'Search files',
    searchFilesPlaceholder: 'Search files…',
    clearFileSearch: 'Clear file search',
    upload: 'Upload',
    uploadFiles: 'Upload files',
    uploadFolder: 'Upload folder',
    invalidUploadFolder: 'This folder contains an invalid path.',
    newFolder: 'New folder',
    folderName: 'Name',
    folderNamePlaceholder: 'e.g. Campaign assets',
    createFolder: 'Create folder',
    renameFolder: 'Rename folder',
    cancel: 'Cancel',
    folderAlreadyExists: 'A file or folder with this name already exists.',
    invalidFolderName: 'Enter a valid folder name.',
    createFolderError: 'Could not create the folder.',
    renameFolderError: 'Could not rename the folder.',
    folderEmpty: 'This folder is empty.',
    noFilesMatchFilter: 'No files match this filter.',
    item: 'item',
    items: 'items',
  },
  'pt-BR': {
    pathAriaLabel: 'Caminho do espa\u00E7o de trabalho',
    personalFiles: 'Minha unidade',
    selectFolder: 'Selecionar pasta',
    personalFilesUnavailable: 'Não foi possível abrir Minha unidade.',
    couldNotLoadFile:
      'N\u00E3o foi poss\u00EDvel carregar o arquivo de espa\u00E7o de trabalho',
    permissionDenied: 'Permiss\u00E3o negada.',
    generationFailed: 'A gera\u00E7\u00E3o falhou.',
    generationInProgress: 'Gera\u00E7\u00E3o em andamento',
    pathNotFound: 'Caminho do espa\u00E7o de trabalho n\u00E3o encontrado',
    openParentFolder: 'Abrir pasta pai',
    openPersonalFiles: 'Abrir Minha unidade',
    tryAgain: 'Tente novamente.',
    addToFavorites: 'Adicionar estrela',
    removeFromFavorites: 'Remover estrela',
    addFavoriteError: 'Não foi possível adicionar a estrela.',
    removeFavoriteError: 'Não foi possível remover a estrela.',
    workspace: 'Espa\u00E7o de trabalho',
    avatars: 'Avatares',
    chats: 'Conversas',
    documents: 'Documentos',
    downloads: 'Transfer\u00EAncias',
    pictures: 'Fotos',
    media: 'Biblioteca de m\u00EDdia',
    mediaEmpty: 'Nenhum arquivo de mídia em Minha unidade ainda.',
    mediaFilterAll: 'Todos',
    mediaKindImage: 'Imagem',
    mediaKindVideo: 'V\u00EDdeo',
    mediaKindAudio: '\u00C1udio',
    mediaKindThreeD: '3D',
    mediaBackToGrid: 'De volta \u00E0 grade',
    shared: 'Compartilhado.',
    sharedWithMe: 'Compartilhado comigo.',
    projects: 'Projetos',
    products: 'Produtos',
    brands: 'Marcas',
    trash: 'Lixo',
    restore: 'Coloque de volta.',
    restoreError: 'N\u00E3o foi poss\u00EDvel restaurar este item.',
    users: 'Usu\u00E1rios',
    fileLocationsAriaLabel: 'Locais de arquivo',
    searchFiles: 'Procurar arquivos',
    searchFilesPlaceholder: 'Procure arquivos...',
    clearFileSearch: 'Limpar busca de arquivos',
    upload: 'Enviar',
    uploadFiles: 'Enviar arquivos',
    uploadFolder: 'Enviar pasta',
    invalidUploadFolder: 'Esta pasta cont\u00E9m um caminho inv\u00E1lido.',
    newFolder: 'Nova pasta',
    folderName: 'Nome',
    folderNamePlaceholder: 'Por exemplo, ativos da campanha.',
    createFolder: 'Criar pasta',
    renameFolder: 'Renomear pasta',
    cancel: 'Cancelar',
    folderAlreadyExists: 'Um arquivo ou pasta com esse nome j\u00E1 existe.',
    invalidFolderName: 'Digite um nome de pasta v\u00E1lido.',
    createFolderError: 'N\u00E3o foi poss\u00EDvel criar a pasta.',
    renameFolderError: 'N\u00E3o foi poss\u00EDvel renomear a pasta.',
    folderEmpty: 'Esta pasta est\u00E1 vazia.',
    noFilesMatchFilter: 'Nenhum arquivo combina com este filtro.',
    item: 'elemento',
    items: 'Itens',
  },
  ar: {
    pathAriaLabel: 'مسار مساحة العمل',
    personalFiles: 'ملفاتي',
    selectFolder: 'اختيار المجلد',
    personalFilesUnavailable: 'تعذّر فتح ملفاتي.',
    couldNotLoadFile: 'تعذّر تحميل ملف مساحة العمل',
    permissionDenied: 'تم رفض الإذن',
    generationFailed: 'فشل الإنشاء',
    generationInProgress: 'الإنشاء قيد التقدم',
    pathNotFound: 'مسار مساحة العمل غير موجود',
    openParentFolder: 'فتح المجلد الأصل',
    openPersonalFiles: 'فتح ملفاتي',
    tryAgain: 'إعادة المحاولة',
    addToFavorites: 'إضافة إلى المميّزة بنجمة',
    removeFromFavorites: 'إزالة من المميّزة بنجمة',
    addFavoriteError: 'تعذّرت الإضافة إلى المميّزة بنجمة.',
    removeFavoriteError: 'تعذّرت الإزالة من المميّزة بنجمة.',
    workspace: 'مساحة العمل',
    avatars: 'الصور الرمزية',
    chats: 'المحادثات',
    documents: 'المستندات',
    downloads: 'التنزيلات',
    pictures: 'الصور',
    media: 'مكتبة الوسائط',
    mediaEmpty: 'لا توجد ملفات وسائط في ملفاتي بعد.',
    mediaFilterAll: 'الكل',
    mediaKindImage: 'صورة',
    mediaKindVideo: 'فيديو',
    mediaKindAudio: 'صوت',
    mediaKindThreeD: 'ثلاثي الأبعاد',
    mediaBackToGrid: 'العودة إلى الشبكة',
    shared: 'المشتركة',
    sharedWithMe: 'تمت مشاركتها معي',
    projects: 'المشاريع',
    products: 'المنتجات',
    brands: 'العلامات التجارية',
    trash: 'سلة المهملات',
    restore: 'استعادة',
    restoreError: 'تعذرت استعادة هذا العنصر.',
    users: 'المستخدمون',
    fileLocationsAriaLabel: 'مواقع الملفات',
    searchFiles: 'البحث في الملفات',
    searchFilesPlaceholder: 'البحث في الملفات…',
    clearFileSearch: 'مسح بحث الملفات',
    upload: 'رفع',
    uploadFiles: 'رفع الملفات',
    uploadFolder: 'رفع مجلد',
    invalidUploadFolder: 'يحتوي هذا المجلد على مسار غير صالح.',
    newFolder: 'مجلد جديد',
    folderName: 'الاسم',
    folderNamePlaceholder: 'مثال: أصول الحملة',
    createFolder: 'إنشاء مجلد',
    renameFolder: 'إعادة تسمية المجلد',
    cancel: 'إلغاء',
    folderAlreadyExists: 'يوجد ملف أو مجلد بهذا الاسم بالفعل.',
    invalidFolderName: 'أدخل اسم مجلد صالحًا.',
    createFolderError: 'تعذّر إنشاء المجلد.',
    renameFolderError: 'تعذّرت إعادة تسمية المجلد.',
    folderEmpty: 'هذا المجلد فارغ.',
    noFilesMatchFilter: 'لا توجد ملفات تطابق عامل التصفية هذا.',
    item: 'عنصر',
    items: 'عناصر',
  },
  de: {
    pathAriaLabel: 'Workspace-Pfad',
    personalFiles: 'Meine Ablage',
    selectFolder: 'Ordner auswählen',
    personalFilesUnavailable: '„Meine Ablage“ konnte nicht geöffnet werden.',
    couldNotLoadFile: 'Workspace-Datei konnte nicht geladen werden',
    permissionDenied: 'Zugriff verweigert',
    generationFailed: 'Generierung fehlgeschlagen',
    generationInProgress: 'Generierung läuft',
    pathNotFound: 'Workspace-Pfad nicht gefunden',
    openParentFolder: 'Übergeordneten Ordner öffnen',
    openPersonalFiles: '„Meine Ablage“ öffnen',
    tryAgain: 'Erneut versuchen',
    addToFavorites: 'Markieren',
    removeFromFavorites: 'Markierung entfernen',
    addFavoriteError: 'Konnte nicht markiert werden.',
    removeFavoriteError: 'Markierung konnte nicht entfernt werden.',
    workspace: 'Workspace',
    avatars: 'Avatare',
    chats: 'Chats',
    documents: 'Dokumente',
    downloads: 'Downloads',
    pictures: 'Bilder',
    media: 'Medienbibliothek',
    mediaEmpty: 'Noch keine Mediendateien in „Meine Ablage“.',
    mediaFilterAll: 'Alle',
    mediaKindImage: 'Bild',
    mediaKindVideo: 'Video',
    mediaKindAudio: 'Audio',
    mediaKindThreeD: '3D',
    mediaBackToGrid: 'Zurück zur Übersicht',
    shared: 'Geteilt',
    sharedWithMe: 'Mit mir geteilt',
    projects: 'Projekte',
    products: 'Produkte',
    brands: 'Marken',
    trash: 'Papierkorb',
    restore: 'Zurücklegen',
    restoreError: 'Dieses Element konnte nicht wiederhergestellt werden.',
    users: 'Benutzer',
    fileLocationsAriaLabel: 'Dateispeicherorte',
    searchFiles: 'Dateien suchen',
    searchFilesPlaceholder: 'Dateien suchen…',
    clearFileSearch: 'Dateisuche löschen',
    upload: 'Hochladen',
    uploadFiles: 'Dateien hochladen',
    uploadFolder: 'Ordner hochladen',
    invalidUploadFolder: 'Dieser Ordner enthält einen ungültigen Pfad.',
    newFolder: 'Neuer Ordner',
    folderName: 'Name',
    folderNamePlaceholder: 'z. B. Kampagnenmaterial',
    createFolder: 'Ordner erstellen',
    renameFolder: 'Ordner umbenennen',
    cancel: 'Abbrechen',
    folderAlreadyExists:
      'Eine Datei oder ein Ordner mit diesem Namen existiert bereits.',
    invalidFolderName: 'Gib einen gültigen Ordnernamen ein.',
    createFolderError: 'Der Ordner konnte nicht erstellt werden.',
    renameFolderError: 'Der Ordner konnte nicht umbenannt werden.',
    folderEmpty: 'Dieser Ordner ist leer.',
    noFilesMatchFilter: 'Keine Dateien entsprechen diesem Filter.',
    item: 'Element',
    items: 'Elemente',
  },
  es: {
    pathAriaLabel: 'Ruta del espacio de trabajo',
    personalFiles: 'Mi unidad',
    selectFolder: 'Seleccionar carpeta',
    personalFilesUnavailable: 'No se pudo abrir Mi unidad.',
    couldNotLoadFile: 'No se pudo cargar el archivo del espacio de trabajo',
    permissionDenied: 'Permiso denegado',
    generationFailed: 'La generación falló',
    generationInProgress: 'Generación en curso',
    pathNotFound: 'No se encontró la ruta del espacio de trabajo',
    openParentFolder: 'Abrir carpeta superior',
    openPersonalFiles: 'Abrir Mi unidad',
    tryAgain: 'Intentar de nuevo',
    addToFavorites: 'Añadir a Destacados',
    removeFromFavorites: 'Quitar de Destacados',
    addFavoriteError: 'No se pudo añadir a Destacados.',
    removeFavoriteError: 'No se pudo quitar de Destacados.',
    workspace: 'Espacio de trabajo',
    avatars: 'Avatares',
    chats: 'Chats',
    documents: 'Documentos',
    downloads: 'Descargas',
    pictures: 'Imágenes',
    media: 'Biblioteca de medios',
    mediaEmpty: 'Aún no hay archivos multimedia en Mi unidad.',
    mediaFilterAll: 'Todos',
    mediaKindImage: 'Imagen',
    mediaKindVideo: 'Vídeo',
    mediaKindAudio: 'Audio',
    mediaKindThreeD: '3D',
    mediaBackToGrid: 'Volver a la cuadrícula',
    shared: 'Compartido',
    sharedWithMe: 'Compartido conmigo',
    projects: 'Proyectos',
    products: 'Productos',
    brands: 'Marcas',
    trash: 'Papelera',
    restore: 'Restaurar',
    restoreError: 'No se pudo restaurar este elemento.',
    users: 'Usuarios',
    fileLocationsAriaLabel: 'Ubicaciones de archivos',
    searchFiles: 'Buscar archivos',
    searchFilesPlaceholder: 'Buscar archivos…',
    clearFileSearch: 'Borrar búsqueda de archivos',
    upload: 'Subir',
    uploadFiles: 'Subir archivos',
    uploadFolder: 'Subir carpeta',
    invalidUploadFolder: 'Esta carpeta contiene una ruta no válida.',
    newFolder: 'Nueva carpeta',
    folderName: 'Nombre',
    folderNamePlaceholder: 'p. ej., Recursos de campaña',
    createFolder: 'Crear carpeta',
    renameFolder: 'Renombrar carpeta',
    cancel: 'Cancelar',
    folderAlreadyExists: 'Ya existe un archivo o una carpeta con este nombre.',
    invalidFolderName: 'Introduce un nombre de carpeta válido.',
    createFolderError: 'No se pudo crear la carpeta.',
    renameFolderError: 'No se pudo renombrar la carpeta.',
    folderEmpty: 'Esta carpeta está vacía.',
    noFilesMatchFilter: 'Ningún archivo coincide con este filtro.',
    item: 'elemento',
    items: 'elementos',
  },
  fr: {
    pathAriaLabel: 'Chemin de l’espace de travail',
    personalFiles: 'Mon Drive',
    selectFolder: 'Sélectionner le dossier',
    personalFilesUnavailable: 'Impossible d’ouvrir Mon Drive.',
    couldNotLoadFile: 'Impossible de charger le fichier de l’espace de travail',
    permissionDenied: 'Autorisation refusée',
    generationFailed: 'Échec de la génération',
    generationInProgress: 'Génération en cours',
    pathNotFound: 'Chemin de l’espace de travail introuvable',
    openParentFolder: 'Ouvrir le dossier parent',
    openPersonalFiles: 'Ouvrir Mon Drive',
    tryAgain: 'Réessayer',
    addToFavorites: 'Ajouter aux suivis',
    removeFromFavorites: 'Retirer des suivis',
    addFavoriteError: 'Impossible d’ajouter aux suivis.',
    removeFavoriteError: 'Impossible de retirer des suivis.',
    workspace: 'Espace de travail',
    avatars: 'Avatars',
    chats: 'Discussions',
    documents: 'Documents',
    downloads: 'Téléchargements',
    pictures: 'Images',
    media: 'Médiathèque',
    mediaEmpty: 'Aucun fichier multimédia dans Mon Drive pour l’instant.',
    mediaFilterAll: 'Tous',
    mediaKindImage: 'Image',
    mediaKindVideo: 'Vidéo',
    mediaKindAudio: 'Audio',
    mediaKindThreeD: '3D',
    mediaBackToGrid: 'Retour à la grille',
    shared: 'Partagés',
    sharedWithMe: 'Partagé avec moi',
    projects: 'Projets',
    products: 'Produits',
    brands: 'Marques',
    trash: 'Corbeille',
    restore: 'Restaurer',
    restoreError: 'Impossible de restaurer cet élément.',
    users: 'Utilisateurs',
    fileLocationsAriaLabel: 'Emplacements des fichiers',
    searchFiles: 'Rechercher des fichiers',
    searchFilesPlaceholder: 'Rechercher des fichiers…',
    clearFileSearch: 'Effacer la recherche de fichiers',
    upload: 'Téléverser',
    uploadFiles: 'Téléverser des fichiers',
    uploadFolder: 'Téléverser un dossier',
    invalidUploadFolder: 'Ce dossier contient un chemin non valide.',
    newFolder: 'Nouveau dossier',
    folderName: 'Nom',
    folderNamePlaceholder: 'p. ex. Ressources de campagne',
    createFolder: 'Créer le dossier',
    renameFolder: 'Renommer le dossier',
    cancel: 'Annuler',
    folderAlreadyExists: 'Un fichier ou un dossier porte déjà ce nom.',
    invalidFolderName: 'Saisissez un nom de dossier valide.',
    createFolderError: 'Impossible de créer le dossier.',
    renameFolderError: 'Impossible de renommer le dossier.',
    folderEmpty: 'Ce dossier est vide.',
    noFilesMatchFilter: 'Aucun fichier ne correspond à ce filtre.',
    item: 'élément',
    items: 'éléments',
  },
  hi: {
    pathAriaLabel: 'वर्कस्पेस पथ',
    personalFiles: 'मेरी ड्राइव',
    selectFolder: 'फ़ोल्डर चुनें',
    personalFilesUnavailable: 'मेरी ड्राइव खोली नहीं जा सकी।',
    couldNotLoadFile: 'वर्कस्पेस फ़ाइल लोड नहीं हो सकी',
    permissionDenied: 'अनुमति अस्वीकृत',
    generationFailed: 'जनरेशन विफल रहा',
    generationInProgress: 'जनरेशन जारी है',
    pathNotFound: 'वर्कस्पेस पथ नहीं मिला',
    openParentFolder: 'पैरेंट फ़ोल्डर खोलें',
    openPersonalFiles: 'मेरी ड्राइव खोलें',
    tryAgain: 'फिर से कोशिश करें',
    addToFavorites: 'तारांकित करें',
    removeFromFavorites: 'तारांकन हटाएँ',
    addFavoriteError: 'तारांकित नहीं किया जा सका।',
    removeFavoriteError: 'तारांकन हटाया नहीं जा सका।',
    workspace: 'वर्कस्पेस',
    avatars: 'अवतार',
    chats: 'चैट',
    documents: 'दस्तावेज़',
    downloads: 'डाउनलोड',
    pictures: 'तस्वीरें',
    media: 'मीडिया लाइब्रेरी',
    mediaEmpty: 'मेरी ड्राइव में अभी कोई मीडिया फ़ाइल नहीं है।',
    mediaFilterAll: 'सभी',
    mediaKindImage: 'छवि',
    mediaKindVideo: 'वीडियो',
    mediaKindAudio: 'ऑडियो',
    mediaKindThreeD: '3D',
    mediaBackToGrid: 'ग्रिड पर वापस जाएँ',
    shared: 'साझा',
    sharedWithMe: 'मेरे साथ साझा',
    projects: 'प्रोजेक्ट',
    products: 'उत्पाद',
    brands: 'ब्रांड',
    trash: 'कचरा',
    restore: 'वापस रखें',
    restoreError: 'यह आइटम पुनर्स्थापित नहीं किया जा सका।',
    users: 'उपयोगकर्ता',
    fileLocationsAriaLabel: 'फ़ाइल स्थान',
    searchFiles: 'फ़ाइलें खोजें',
    searchFilesPlaceholder: 'फ़ाइलें खोजें…',
    clearFileSearch: 'फ़ाइल खोज साफ़ करें',
    upload: 'अपलोड करें',
    uploadFiles: 'फ़ाइलें अपलोड करें',
    uploadFolder: 'फ़ोल्डर अपलोड करें',
    invalidUploadFolder: 'इस फ़ोल्डर में एक अमान्य पथ है।',
    newFolder: 'नया फ़ोल्डर',
    folderName: 'नाम',
    folderNamePlaceholder: 'जैसे, कैंपेन एसेट्स',
    createFolder: 'फ़ोल्डर बनाएँ',
    renameFolder: 'फ़ोल्डर का नाम बदलें',
    cancel: 'रद्द करें',
    folderAlreadyExists: 'इस नाम की फ़ाइल या फ़ोल्डर पहले से मौजूद है।',
    invalidFolderName: 'एक मान्य फ़ोल्डर नाम दर्ज करें।',
    createFolderError: 'फ़ोल्डर नहीं बनाया जा सका।',
    renameFolderError: 'फ़ोल्डर का नाम नहीं बदला जा सका।',
    folderEmpty: 'यह फ़ोल्डर खाली है।',
    noFilesMatchFilter: 'इस फ़िल्टर से कोई फ़ाइल मेल नहीं खाती।',
    item: 'आइटम',
    items: 'आइटम',
  },
  it: {
    pathAriaLabel: 'Percorso del workspace',
    personalFiles: 'Il mio Drive',
    selectFolder: 'Seleziona cartella',
    personalFilesUnavailable: 'Impossibile aprire Il mio Drive.',
    couldNotLoadFile: 'Impossibile caricare il file del workspace',
    permissionDenied: 'Autorizzazione negata',
    generationFailed: 'Generazione non riuscita',
    generationInProgress: 'Generazione in corso',
    pathNotFound: 'Percorso del workspace non trovato',
    openParentFolder: 'Apri cartella superiore',
    openPersonalFiles: 'Apri Il mio Drive',
    tryAgain: 'Riprova',
    addToFavorites: 'Aggiungi a Speciali',
    removeFromFavorites: 'Rimuovi da Speciali',
    addFavoriteError: 'Impossibile aggiungere a Speciali.',
    removeFavoriteError: 'Impossibile rimuovere da Speciali.',
    workspace: 'Workspace',
    avatars: 'Avatar',
    chats: 'Chat',
    documents: 'Documenti',
    downloads: 'Download',
    pictures: 'Immagini',
    media: 'Libreria media',
    mediaEmpty: 'Ancora nessun file multimediale in Il mio Drive.',
    mediaFilterAll: 'Tutti',
    mediaKindImage: 'Immagine',
    mediaKindVideo: 'Video',
    mediaKindAudio: 'Audio',
    mediaKindThreeD: '3D',
    mediaBackToGrid: 'Torna alla griglia',
    shared: 'Condivisi',
    sharedWithMe: 'Condivisi con me',
    projects: 'Progetti',
    products: 'Prodotti',
    brands: 'Brand',
    trash: 'Cestino',
    restore: 'Ripristina',
    restoreError: 'Impossibile ripristinare questo elemento.',
    users: 'Utenti',
    fileLocationsAriaLabel: 'Posizioni dei file',
    searchFiles: 'Cerca file',
    searchFilesPlaceholder: 'Cerca file…',
    clearFileSearch: 'Cancella ricerca file',
    upload: 'Carica',
    uploadFiles: 'Carica file',
    uploadFolder: 'Carica cartella',
    invalidUploadFolder: 'Questa cartella contiene un percorso non valido.',
    newFolder: 'Nuova cartella',
    folderName: 'Nome',
    folderNamePlaceholder: 'es. Risorse campagna',
    createFolder: 'Crea cartella',
    renameFolder: 'Rinomina cartella',
    cancel: 'Annulla',
    folderAlreadyExists: 'Esiste già un file o una cartella con questo nome.',
    invalidFolderName: 'Inserisci un nome di cartella valido.',
    createFolderError: 'Impossibile creare la cartella.',
    renameFolderError: 'Impossibile rinominare la cartella.',
    folderEmpty: 'Questa cartella è vuota.',
    noFilesMatchFilter: 'Nessun file corrisponde a questo filtro.',
    item: 'elemento',
    items: 'elementi',
  },
  ja: {
    pathAriaLabel: 'ワークスペースのパス',
    personalFiles: 'マイドライブ',
    selectFolder: 'フォルダーを選択',
    personalFilesUnavailable: 'マイドライブを開けませんでした。',
    couldNotLoadFile: 'ワークスペースファイルを読み込めませんでした',
    permissionDenied: '権限がありません',
    generationFailed: '生成に失敗しました',
    generationInProgress: '生成中です',
    pathNotFound: 'ワークスペースのパスが見つかりません',
    openParentFolder: '親フォルダーを開く',
    openPersonalFiles: 'マイドライブを開く',
    tryAgain: '再試行',
    addToFavorites: 'スターを付ける',
    removeFromFavorites: 'スターを外す',
    addFavoriteError: 'スターを付けられませんでした。',
    removeFavoriteError: 'スターを外せませんでした。',
    workspace: 'ワークスペース',
    avatars: 'アバター',
    chats: 'チャット',
    documents: '書類',
    downloads: 'ダウンロード',
    pictures: '画像',
    media: 'メディアライブラリ',
    mediaEmpty: 'マイドライブにメディアファイルはまだありません。',
    mediaFilterAll: 'すべて',
    mediaKindImage: '画像',
    mediaKindVideo: '動画',
    mediaKindAudio: '音声',
    mediaKindThreeD: '3D',
    mediaBackToGrid: 'グリッドに戻る',
    shared: '共有',
    sharedWithMe: '自分と共有',
    projects: 'プロジェクト',
    products: '製品',
    brands: 'ブランド',
    trash: 'ゴミ箱',
    restore: '元に戻す',
    restoreError: 'この項目を復元できませんでした。',
    users: 'ユーザー',
    fileLocationsAriaLabel: 'ファイルの場所',
    searchFiles: 'ファイルを検索',
    searchFilesPlaceholder: 'ファイルを検索…',
    clearFileSearch: 'ファイル検索をクリア',
    upload: 'アップロード',
    uploadFiles: 'ファイルをアップロード',
    uploadFolder: 'フォルダーをアップロード',
    invalidUploadFolder: 'このフォルダーには無効なパスが含まれています。',
    newFolder: '新規フォルダー',
    folderName: '名前',
    folderNamePlaceholder: '例：キャンペーン素材',
    createFolder: 'フォルダーを作成',
    renameFolder: 'フォルダー名を変更',
    cancel: 'キャンセル',
    folderAlreadyExists: '同じ名前のファイルまたはフォルダーが既にあります。',
    invalidFolderName: '有効なフォルダー名を入力してください。',
    createFolderError: 'フォルダーを作成できませんでした。',
    renameFolderError: 'フォルダー名を変更できませんでした。',
    folderEmpty: 'このフォルダーは空です。',
    noFilesMatchFilter: 'このフィルターに一致するファイルはありません。',
    item: '項目',
    items: '項目',
  },
  ko: {
    pathAriaLabel: '워크스페이스 경로',
    personalFiles: '내 드라이브',
    selectFolder: '폴더 선택',
    personalFilesUnavailable: '내 드라이브를 열 수 없습니다.',
    couldNotLoadFile: '워크스페이스 파일을 불러올 수 없습니다',
    permissionDenied: '권한이 없습니다',
    generationFailed: '생성에 실패했습니다',
    generationInProgress: '생성 중입니다',
    pathNotFound: '워크스페이스 경로를 찾을 수 없습니다',
    openParentFolder: '상위 폴더 열기',
    openPersonalFiles: '내 드라이브 열기',
    tryAgain: '다시 시도',
    addToFavorites: '중요 표시',
    removeFromFavorites: '중요 표시 해제',
    addFavoriteError: '중요 표시하지 못했습니다.',
    removeFavoriteError: '중요 표시를 해제하지 못했습니다.',
    workspace: '워크스페이스',
    avatars: '아바타',
    chats: '채팅',
    documents: '문서',
    downloads: '다운로드',
    pictures: '사진',
    media: '미디어 라이브러리',
    mediaEmpty: '내 드라이브에 미디어 파일이 아직 없습니다.',
    mediaFilterAll: '전체',
    mediaKindImage: '이미지',
    mediaKindVideo: '동영상',
    mediaKindAudio: '오디오',
    mediaKindThreeD: '3D',
    mediaBackToGrid: '그리드로 돌아가기',
    shared: '공유됨',
    sharedWithMe: '나와 공유됨',
    projects: '프로젝트',
    products: '제품',
    brands: '브랜드',
    trash: '휴지통',
    restore: '되돌리기',
    restoreError: '이 항목을 복원할 수 없습니다.',
    users: '사용자',
    fileLocationsAriaLabel: '파일 위치',
    searchFiles: '파일 검색',
    searchFilesPlaceholder: '파일 검색…',
    clearFileSearch: '파일 검색 지우기',
    upload: '업로드',
    uploadFiles: '파일 업로드',
    uploadFolder: '폴더 업로드',
    invalidUploadFolder: '이 폴더에 잘못된 경로가 포함되어 있습니다.',
    newFolder: '새 폴더',
    folderName: '이름',
    folderNamePlaceholder: '예: 캠페인 자료',
    createFolder: '폴더 만들기',
    renameFolder: '폴더 이름 변경',
    cancel: '취소',
    folderAlreadyExists: '같은 이름의 파일 또는 폴더가 이미 있습니다.',
    invalidFolderName: '올바른 폴더 이름을 입력하세요.',
    createFolderError: '폴더를 만들 수 없습니다.',
    renameFolderError: '폴더 이름을 변경할 수 없습니다.',
    folderEmpty: '이 폴더는 비어 있습니다.',
    noFilesMatchFilter: '이 필터와 일치하는 파일이 없습니다.',
    item: '항목',
    items: '항목',
  },
  ru: {
    pathAriaLabel: 'Путь в рабочем пространстве',
    personalFiles: 'Мой диск',
    selectFolder: 'Выбрать папку',
    personalFilesUnavailable: 'Не удалось открыть «Мой диск».',
    couldNotLoadFile: 'Не удалось загрузить файл рабочего пространства',
    permissionDenied: 'Доступ запрещён',
    generationFailed: 'Ошибка генерации',
    generationInProgress: 'Идёт генерация',
    pathNotFound: 'Путь в рабочем пространстве не найден',
    openParentFolder: 'Открыть родительскую папку',
    openPersonalFiles: 'Открыть «Мой диск»',
    tryAgain: 'Повторить',
    addToFavorites: 'Пометить',
    removeFromFavorites: 'Снять пометку',
    addFavoriteError: 'Не удалось пометить.',
    removeFavoriteError: 'Не удалось снять пометку.',
    workspace: 'Рабочее пространство',
    avatars: 'Аватары',
    chats: 'Чаты',
    documents: 'Документы',
    downloads: 'Загрузки',
    pictures: 'Изображения',
    media: 'Медиатека',
    mediaEmpty: 'На «Моём диске» пока нет медиафайлов.',
    mediaFilterAll: 'Все',
    mediaKindImage: 'Изображение',
    mediaKindVideo: 'Видео',
    mediaKindAudio: 'Аудио',
    mediaKindThreeD: '3D',
    mediaBackToGrid: 'Назад к сетке',
    shared: 'Общие',
    sharedWithMe: 'Доступные мне',
    projects: 'Проекты',
    products: 'Продукты',
    brands: 'Бренды',
    trash: 'Корзина',
    restore: 'Восстановить',
    restoreError: 'Не удалось восстановить этот элемент.',
    users: 'Пользователи',
    fileLocationsAriaLabel: 'Расположение файлов',
    searchFiles: 'Поиск файлов',
    searchFilesPlaceholder: 'Поиск файлов…',
    clearFileSearch: 'Очистить поиск файлов',
    upload: 'Загрузить',
    uploadFiles: 'Загрузить файлы',
    uploadFolder: 'Загрузить папку',
    invalidUploadFolder: 'Эта папка содержит недопустимый путь.',
    newFolder: 'Новая папка',
    folderName: 'Название',
    folderNamePlaceholder: 'например, Материалы кампании',
    createFolder: 'Создать папку',
    renameFolder: 'Переименовать папку',
    cancel: 'Отмена',
    folderAlreadyExists: 'Файл или папка с таким названием уже существует.',
    invalidFolderName: 'Введите допустимое название папки.',
    createFolderError: 'Не удалось создать папку.',
    renameFolderError: 'Не удалось переименовать папку.',
    folderEmpty: 'Эта папка пуста.',
    noFilesMatchFilter: 'Нет файлов, соответствующих этому фильтру.',
    item: 'элемент',
    items: 'элементов',
  },
  zh: {
    pathAriaLabel: '工作区路径',
    personalFiles: '我的云端硬盘',
    selectFolder: '选择文件夹',
    personalFilesUnavailable: '无法打开我的云端硬盘。',
    couldNotLoadFile: '无法加载工作区文件',
    permissionDenied: '权限不足',
    generationFailed: '生成失败',
    generationInProgress: '正在生成',
    pathNotFound: '未找到工作区路径',
    openParentFolder: '打开上级文件夹',
    openPersonalFiles: '打开我的云端硬盘',
    tryAgain: '重试',
    addToFavorites: '加星标',
    removeFromFavorites: '移除星标',
    addFavoriteError: '无法加星标。',
    removeFavoriteError: '无法移除星标。',
    workspace: '工作区',
    avatars: '头像',
    chats: '聊天',
    documents: '文档',
    downloads: '下载',
    pictures: '图片',
    media: '媒体库',
    mediaEmpty: '我的云端硬盘中还没有媒体文件。',
    mediaFilterAll: '全部',
    mediaKindImage: '图片',
    mediaKindVideo: '视频',
    mediaKindAudio: '音频',
    mediaKindThreeD: '3D',
    mediaBackToGrid: '返回网格',
    shared: '共享',
    sharedWithMe: '共享给我的',
    projects: '项目',
    products: '产品',
    brands: '品牌',
    trash: '垃圾桶',
    restore: '恢复',
    restoreError: '无法恢复此项目。',
    users: '用户',
    fileLocationsAriaLabel: '文件位置',
    searchFiles: '搜索文件',
    searchFilesPlaceholder: '搜索文件…',
    clearFileSearch: '清除文件搜索',
    upload: '上传',
    uploadFiles: '上传文件',
    uploadFolder: '上传文件夹',
    invalidUploadFolder: '此文件夹包含无效路径。',
    newFolder: '新建文件夹',
    folderName: '名称',
    folderNamePlaceholder: '例如：活动素材',
    createFolder: '创建文件夹',
    renameFolder: '重命名文件夹',
    cancel: '取消',
    folderAlreadyExists: '已存在同名文件或文件夹。',
    invalidFolderName: '请输入有效的文件夹名称。',
    createFolderError: '无法创建文件夹。',
    renameFolderError: '无法重命名文件夹。',
    folderEmpty: '此文件夹为空。',
    noFilesMatchFilter: '没有文件符合此筛选条件。',
    item: '个项目',
    items: '个项目',
  },
  'zh-TW': {
    pathAriaLabel: '工作區路徑',
    personalFiles: '我的雲端硬碟',
    selectFolder: '選擇資料夾',
    personalFilesUnavailable: '無法開啟我的雲端硬碟。',
    couldNotLoadFile: '無法載入工作區檔案',
    permissionDenied: '權限不足',
    generationFailed: '生成失敗',
    generationInProgress: '正在生成',
    pathNotFound: '找不到工作區路徑',
    openParentFolder: '開啟上層資料夾',
    openPersonalFiles: '開啟我的雲端硬碟',
    tryAgain: '再試一次',
    addToFavorites: '加上星號',
    removeFromFavorites: '移除星號',
    addFavoriteError: '無法加上星號。',
    removeFavoriteError: '無法移除星號。',
    workspace: '工作區',
    avatars: '虛擬人像',
    chats: '聊天',
    documents: '文件',
    downloads: '下載項目',
    pictures: '圖片',
    media: '媒體庫',
    mediaEmpty: '我的雲端硬碟中還沒有媒體檔案。',
    mediaFilterAll: '全部',
    mediaKindImage: '圖片',
    mediaKindVideo: '影片',
    mediaKindAudio: '音訊',
    mediaKindThreeD: '3D',
    mediaBackToGrid: '返回網格',
    shared: '共享',
    sharedWithMe: '共用給我的',
    projects: '專案',
    products: '產品',
    brands: '品牌',
    trash: '垃圾桶',
    restore: '回復',
    restoreError: '無法回復此項目。',
    users: '使用者',
    fileLocationsAriaLabel: '檔案位置',
    searchFiles: '搜尋檔案',
    searchFilesPlaceholder: '搜尋檔案…',
    clearFileSearch: '清除檔案搜尋',
    upload: '上傳',
    uploadFiles: '上傳檔案',
    uploadFolder: '上傳資料夾',
    invalidUploadFolder: '此資料夾包含無效路徑。',
    newFolder: '新增資料夾',
    folderName: '名稱',
    folderNamePlaceholder: '例如：活動素材',
    createFolder: '建立資料夾',
    renameFolder: '重新命名資料夾',
    cancel: '取消',
    folderAlreadyExists: '已存在同名檔案或資料夾。',
    invalidFolderName: '請輸入有效的資料夾名稱。',
    createFolderError: '無法建立資料夾。',
    renameFolderError: '無法重新命名資料夾。',
    folderEmpty: '此資料夾是空的。',
    noFilesMatchFilter: '沒有檔案符合此篩選條件。',
    item: '個項目',
    items: '個項目',
  },
  vi: {
    pathAriaLabel: 'Đường dẫn không gian làm việc',
    personalFiles: 'Drive của tôi',
    selectFolder: 'Chọn thư mục',
    personalFilesUnavailable: 'Không thể mở Drive của tôi.',
    couldNotLoadFile: 'Không thể tải tệp trong không gian làm việc',
    permissionDenied: 'Quyền truy cập bị từ chối',
    generationFailed: 'Tạo nội dung thất bại',
    generationInProgress: 'Đang tạo nội dung',
    pathNotFound: 'Không tìm thấy đường dẫn không gian làm việc',
    openParentFolder: 'Mở thư mục cha',
    openPersonalFiles: 'Mở Drive của tôi',
    tryAgain: 'Thử lại',
    addToFavorites: 'Gắn dấu sao',
    removeFromFavorites: 'Bỏ dấu sao',
    addFavoriteError: 'Không thể gắn dấu sao.',
    removeFavoriteError: 'Không thể bỏ dấu sao.',
    workspace: 'Không gian làm việc',
    avatars: 'Ảnh đại diện',
    chats: 'Cuộc trò chuyện',
    documents: 'Tài liệu',
    downloads: 'Tệp tải xuống',
    pictures: 'Hình ảnh',
    media: 'Thư viện phương tiện',
    mediaEmpty: 'Chưa có tệp phương tiện nào trong Drive của tôi.',
    mediaFilterAll: 'Tất cả',
    mediaKindImage: 'Hình ảnh',
    mediaKindVideo: 'Video',
    mediaKindAudio: 'Âm thanh',
    mediaKindThreeD: '3D',
    mediaBackToGrid: 'Quay lại lưới',
    shared: 'Được chia sẻ',
    sharedWithMe: 'Được chia sẻ với tôi',
    projects: 'Dự án',
    products: 'Sản phẩm',
    brands: 'Thương hiệu',
    trash: 'Thùng rác',
    restore: 'Khôi phục',
    restoreError: 'Không thể khôi phục mục này.',
    users: 'Người dùng',
    fileLocationsAriaLabel: 'Vị trí tệp',
    searchFiles: 'Tìm kiếm tệp',
    searchFilesPlaceholder: 'Tìm kiếm tệp…',
    clearFileSearch: 'Xóa tìm kiếm tệp',
    upload: 'Tải lên',
    uploadFiles: 'Tải tệp lên',
    uploadFolder: 'Tải thư mục lên',
    invalidUploadFolder: 'Thư mục này chứa đường dẫn không hợp lệ.',
    newFolder: 'Thư mục mới',
    folderName: 'Tên',
    folderNamePlaceholder: 'ví dụ: Tài nguyên chiến dịch',
    createFolder: 'Tạo thư mục',
    renameFolder: 'Đổi tên thư mục',
    cancel: 'Hủy',
    folderAlreadyExists: 'Đã có tệp hoặc thư mục mang tên này.',
    invalidFolderName: 'Nhập tên thư mục hợp lệ.',
    createFolderError: 'Không thể tạo thư mục.',
    renameFolderError: 'Không thể đổi tên thư mục.',
    folderEmpty: 'Thư mục này trống.',
    noFilesMatchFilter: 'Không có tệp nào khớp với bộ lọc này.',
    item: 'mục',
    items: 'mục',
  },
  id: {
    pathAriaLabel: 'Jalur workspace',
    personalFiles: 'Drive Saya',
    selectFolder: 'Pilih folder',
    personalFilesUnavailable: 'Tidak dapat membuka Drive Saya.',
    couldNotLoadFile: 'File workspace tidak dapat dimuat',
    permissionDenied: 'Izin ditolak',
    generationFailed: 'Pembuatan gagal',
    generationInProgress: 'Pembuatan sedang berlangsung',
    pathNotFound: 'Jalur workspace tidak ditemukan',
    openParentFolder: 'Buka folder induk',
    openPersonalFiles: 'Buka Drive Saya',
    tryAgain: 'Coba lagi',
    addToFavorites: 'Beri bintang',
    removeFromFavorites: 'Hapus bintang',
    addFavoriteError: 'Tidak dapat memberi bintang.',
    removeFavoriteError: 'Tidak dapat menghapus bintang.',
    workspace: 'Workspace',
    avatars: 'Avatar',
    chats: 'Obrolan',
    documents: 'Dokumen',
    downloads: 'Unduhan',
    pictures: 'Gambar',
    media: 'Perpustakaan media',
    mediaEmpty: 'Belum ada file media di Drive Saya.',
    mediaFilterAll: 'Semua',
    mediaKindImage: 'Gambar',
    mediaKindVideo: 'Video',
    mediaKindAudio: 'Audio',
    mediaKindThreeD: '3D',
    mediaBackToGrid: 'Kembali ke kisi',
    shared: 'Dibagikan',
    sharedWithMe: 'Dibagikan dengan saya',
    projects: 'Proyek',
    products: 'Produk',
    brands: 'Merek',
    trash: 'Sampah',
    restore: 'Pulihkan',
    restoreError: 'Item ini tidak dapat dipulihkan.',
    users: 'Pengguna',
    fileLocationsAriaLabel: 'Lokasi file',
    searchFiles: 'Cari file',
    searchFilesPlaceholder: 'Cari file…',
    clearFileSearch: 'Hapus pencarian file',
    upload: 'Unggah',
    uploadFiles: 'Unggah file',
    uploadFolder: 'Unggah folder',
    invalidUploadFolder: 'Folder ini berisi jalur yang tidak valid.',
    newFolder: 'Folder baru',
    folderName: 'Nama',
    folderNamePlaceholder: 'mis. Aset kampanye',
    createFolder: 'Buat folder',
    renameFolder: 'Ubah nama folder',
    cancel: 'Batal',
    folderAlreadyExists: 'File atau folder dengan nama ini sudah ada.',
    invalidFolderName: 'Masukkan nama folder yang valid.',
    createFolderError: 'Folder tidak dapat dibuat.',
    renameFolderError: 'Folder tidak dapat diubah namanya.',
    folderEmpty: 'Folder ini kosong.',
    noFilesMatchFilter: 'Tidak ada file yang cocok dengan filter ini.',
    item: 'butir',
    items: 'butir',
  },
};

const TABLE_COPY_BY_LOCALE: Record<SupportedLocale, WorkspaceFilesTableCopy> = {
  en: {
    name: 'Name',
    source: 'Source',
    created: 'Created',
    modified: 'Modified',
    kind: 'Kind',
    size: 'Size',
    directory: 'Directory',
    uploads: 'Uploads',
  },
  'pt-BR': {
    name: 'Nome',
    source: 'Fonte',
    created: 'Criado',
    modified: 'Modificado',
    kind: 'Tipo',
    size: 'Tamanho',
    directory: 'Diret\u00F3rio',
    uploads: 'Envios',
  },
  ar: {
    name: 'الاسم',
    source: 'المصدر',
    created: 'تاريخ الإنشاء',
    modified: 'تاريخ التعديل',
    kind: 'النوع',
    size: 'الحجم',
    directory: 'مجلد',
    uploads: 'عمليات الرفع',
  },
  de: {
    name: 'Name',
    source: 'Quelle',
    created: 'Erstellt',
    modified: 'Geändert',
    kind: 'Art',
    size: 'Größe',
    directory: 'Ordner',
    uploads: 'Uploads',
  },
  es: {
    name: 'Nombre',
    source: 'Origen',
    created: 'Creado',
    modified: 'Modificado',
    kind: 'Tipo',
    size: 'Tamaño',
    directory: 'Carpeta',
    uploads: 'Subidas',
  },
  fr: {
    name: 'Nom',
    source: 'Source',
    created: 'Créé',
    modified: 'Modifié',
    kind: 'Type',
    size: 'Taille',
    directory: 'Dossier',
    uploads: 'Téléversements',
  },
  hi: {
    name: 'नाम',
    source: 'स्रोत',
    created: 'बनाया गया',
    modified: 'संशोधित',
    kind: 'प्रकार',
    size: 'आकार',
    directory: 'फ़ोल्डर',
    uploads: 'अपलोड',
  },
  it: {
    name: 'Nome',
    source: 'Origine',
    created: 'Creato',
    modified: 'Modificato',
    kind: 'Tipo',
    size: 'Dimensione',
    directory: 'Cartella',
    uploads: 'Caricamenti',
  },
  ja: {
    name: '名前',
    source: 'ソース',
    created: '作成日',
    modified: '更新日',
    kind: '種類',
    size: 'サイズ',
    directory: 'フォルダ',
    uploads: 'アップロード',
  },
  ko: {
    name: '이름',
    source: '소스',
    created: '생성일',
    modified: '수정일',
    kind: '종류',
    size: '크기',
    directory: '폴더',
    uploads: '업로드',
  },
  ru: {
    name: 'Имя',
    source: 'Источник',
    created: 'Создано',
    modified: 'Изменено',
    kind: 'Тип',
    size: 'Размер',
    directory: 'Папка',
    uploads: 'Загрузки',
  },
  zh: {
    name: '名称',
    source: '来源',
    created: '创建时间',
    modified: '修改时间',
    kind: '类型',
    size: '大小',
    directory: '文件夹',
    uploads: '上传',
  },
  'zh-TW': {
    name: '名稱',
    source: '來源',
    created: '建立時間',
    modified: '修改時間',
    kind: '類型',
    size: '大小',
    directory: '資料夾',
    uploads: '上傳',
  },
  vi: {
    name: 'Tên',
    source: 'Nguồn',
    created: 'Ngày tạo',
    modified: 'Ngày sửa',
    kind: 'Loại',
    size: 'Kích thước',
    directory: 'Thư mục',
    uploads: 'Tệp tải lên',
  },
  id: {
    name: 'Nama',
    source: 'Sumber',
    created: 'Dibuat',
    modified: 'Diubah',
    kind: 'Jenis',
    size: 'Ukuran',
    directory: 'Folder',
    uploads: 'Unggahan',
  },
};

export function getWorkspaceFilesStaticFolderLabel(
  name: string,
  copy: WorkspaceFilesCopy & WorkspaceFilesTableCopy
): string | null {
  const labels: Record<string, string> = {
    Avatars: copy.avatars,
    Brands: copy.brands,
    Chats: copy.chats,
    Documents: copy.documents,
    Downloads: copy.downloads,
    Pictures: copy.pictures,
    Products: copy.products,
    Projects: copy.projects,
    Shared: copy.shared,
    Uploads: copy.uploads,
  };
  return labels[name] ?? null;
}

export function getWorkspaceFilesCopy(
  locale: string | null | undefined
): WorkspaceFilesCopy & WorkspaceFilesTableCopy {
  const normalizedLocale = normalizeLocale(locale);
  return {
    ...COPY_BY_LOCALE[normalizedLocale],
    ...TABLE_COPY_BY_LOCALE[normalizedLocale],
  };
}
